import {
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from '@revelio/core/composition-buckets';

import type { CompositionMetricValues } from '../../../PageConfigProvider/types/metric';
import {
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../../types';
import type { CompositionV3GraphqlResponse } from './composition-v3-graphql-response';

const entityLabel = (
  rowId: number | string | null | undefined,
  shortName: string
): string => (isOtherCompositionBucket(rowId) ? OTHER_BUCKET_LABEL : shortName);

type CompositionV3Payload = CompositionV3GraphqlResponse;

type MultiCompositionDataV3 = {
  data: CompositionV3Payload | null | undefined;
};

export const convertCompositionTopNDataV3 = ({
  data,
  metric,
  exchangeRate = 1,
}: {
  data: MultiCompositionDataV3[];
  metric: CompositionMetricValues;
  exchangeRate?: number;
}): SingleDataSet => {
  // Same-named segments repeat across primaries (Engineer at Google AND at
  // Microsoft). Only headcount is additive; rates/salary/tenure must be
  // averaged, weighted by the segment's latest headcount within each primary
  // (10% of 1,000 people + 20% of 3,000 ≠ 30%). A row without a headcount
  // series contributes weight 1, so an all-weightless merge degrades to a
  // simple mean.
  const isAdditiveMetric = metric === 'headcount';
  const mergeAcc = new Map<
    string,
    {
      point: SingleDataPoint;
      weightedSum: number;
      weightSum: number;
      /** Summed real headcounts (undefined until one is seen). */
      headcountSum?: number;
    }
  >();
  data?.forEach(({ data: payload }) => {
    payload?.entities?.forEach((entity) => {
      const metricData = entity.metrics?.[metric];
      const lastTimeseries = metricData?.[metricData.length - 1];
      if (!lastTimeseries) {
        return;
      }
      const convertedValue =
        metric === 'salary'
          ? lastTimeseries.val * exchangeRate
          : lastTimeseries.val;
      const headcountData = entity.metrics?.headcount;
      const lastHeadcount = headcountData?.[headcountData.length - 1]?.val;
      const realHeadcount =
        typeof lastHeadcount === 'number' && lastHeadcount > 0
          ? lastHeadcount
          : undefined;
      const weight = realHeadcount ?? 1;

      const innerDim = entityLabel(entity.id, entity.short_name);
      const existing = mergeAcc.get(innerDim);
      if (!existing) {
        mergeAcc.set(innerDim, {
          point: {
            dim: innerDim,
            value: convertedValue,
            // Segment EntID (absent for the residual Other bucket) — the
            // assistant's breakdown digest reads it to rebuild filters.
            ...(entity.id != null &&
              !isOtherCompositionBucket(entity.id) && {
                id: String(entity.id),
              }),
            // Size context for rate metrics; the plotted value stays the rate.
            ...(!isAdditiveMetric &&
              realHeadcount !== undefined && {
                segmentHeadcount: realHeadcount,
              }),
          },
          weightedSum: convertedValue * weight,
          weightSum: weight,
          ...(realHeadcount !== undefined && { headcountSum: realHeadcount }),
        });
        return;
      }
      existing.weightedSum += convertedValue * weight;
      existing.weightSum += weight;
      existing.point.value = isAdditiveMetric
        ? (existing.point.value ?? 0) + convertedValue
        : existing.weightedSum / existing.weightSum;
      // Sizes ARE additive across primaries even when the rate is weighted.
      if (realHeadcount !== undefined) {
        existing.headcountSum = (existing.headcountSum ?? 0) + realHeadcount;
        if (!isAdditiveMetric) {
          existing.point.segmentHeadcount = existing.headcountSum;
        }
      }
    });
  });

  const barData: SingleDataSet['data'] = [...mergeAcc.values()]
    .map(({ point }) => point)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  return { type: DataType.SINGLE, data: barData };
};
